/**
 * The small lettered, coloured circle shown next to a dimension (W for width, L for length, OD,
 * ID, Side A, ...). Used beside the
 * define-cut modal's inputs and attribute list
 * (`components/Shared/DefineCutModal/DefineCutModalDesktop.tsx`,
 * `DefineCutModalMobile.tsx`) and the catalog guided-view filters
 * (`components/Catalog/CatalogProductsDetails/CatalogProductsGuidedView/`).
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Icon, IconList } from "@ryerson/frontend.assets";
import styled from "@emotion/styled";

/** Props of {@link AttributeIcon}. */
export type AttributeIconType = {
	/**
	 * A cut field name (`width`, `oneSide`, `outsideDiameter`) or a product attribute or filter
	 * label (`Side A`, `Wall 1 - Inch`, `OD`). Matched case-insensitively.
	 */
	attribute: string;
	/**
	 * The product shape. For `circle` or `ring`, the wall, width and length icons use the
	 * disabled colour. Only the catalog passes it.
	 */
	shape?: string;
};

const IconContainer = styled.div`
	padding-left: 0px;
`;

/** Renders the icon for a known dimension, and nothing for any other attribute. */
const AttributeIcon: React.FC<AttributeIconType> = ({ attribute, shape }) => {
	const { theme } = useTheme();
	const { success, disable, lightBlue, pink, purple, yellow, error, darkGreen } =
		theme.colors.secondary;

	let icon = "" as keyof IconList;
	let iconColor = "";
	let displayIcon = false;
	let disableColor = shape?.toLowerCase() === "circle" || shape?.toLowerCase() === "ring";

	// Labels keep only their first word ("wall 1 - inch" becomes "wall", "id - inch" becomes "id"),
	// except those containing "side", which are kept whole so "side a" / "side b" and the cut
	// fields "oneside", "outsidediameter" and "insidediameter" match. A spaced label such as
	// "outside diameter" therefore matches no case.
	const formattedAttribute = (function () {
		let formattedAttribute = attribute?.toLowerCase();
		if (formattedAttribute?.includes("side")) {
			return formattedAttribute;
		}
		return formattedAttribute?.replace(/[\s-].*/, "");
	})();

	switch (formattedAttribute) {
		case "depth":
			icon = "text-d";
			iconColor = error;
			displayIcon = true;
			break;
		case "hole":
			icon = "text-h";
			iconColor = error;
			displayIcon = true;
			break;
		case "side":
			icon = "text-s";
			iconColor = darkGreen;
			displayIcon = true;
			break;
		case "side b":
			icon = "text-b";
			iconColor = yellow;
			displayIcon = true;
			break;
		case "side a":
			icon = "text-a";
			iconColor = purple;
			displayIcon = true;
			break;
		case "nominal":
			icon = "text-n";
			iconColor = success;
			displayIcon = true;
			break;
		case "thickness":
			icon = "text-t";
			iconColor = success;
			displayIcon = true;
			break;
		case "wall":
		case "width":
			icon = "text-w";
			iconColor = disableColor ? disable : yellow;
			displayIcon = true;
			break;
		case "length":
			icon = "text-l";
			iconColor = disableColor ? disable : lightBlue;
			displayIcon = true;
			break;
		case "oneside":
			icon = "text-s";
			iconColor = success;
			displayIcon = true;
			break;
		case "diameter":
			icon = "text-od";
			iconColor = pink;
			displayIcon = true;
			break;
		case "od":
		case "outsidediameter":
			icon = "text-od";
			iconColor = pink;
			displayIcon = true;
			break;
		case "id":
		case "insidediameter":
			icon = "text-id";
			iconColor = purple;
			displayIcon = true;
			break;
		default:
			displayIcon = false;
	}

	return (
		<>
			{displayIcon && (
				<IconContainer>
					<Icon icon={icon} color={iconColor} circled={true} size="sm" />
				</IconContainer>
			)}
		</>
	);
};

export default AttributeIcon;
